function toNumber(value) {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? numericValue : 0
}

function normalizeDate(value) {
    if (!value) return null
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
}

function getEntryQuantity(entry = {}) {
    return toNumber(entry.stock_value) + toNumber(entry.stock_value_back_room)
}

function getEntryValue(entry = {}) {
    return getEntryQuantity(entry) * toNumber(entry.valuation_rate)
}

function getWarehouseName(warehouseId, warehouses = []) {
    const warehouse = warehouses.find(item => String(item.id) === String(warehouseId))
    return warehouse?.name || warehouse?.title || warehouseId || "Unknown Warehouse"
}

function flattenLedgerEntries(products = []) {
    return products.flatMap((product) => {
        const entries = Array.isArray(product.ledgerEntries) ? product.ledgerEntries : []
        return entries.map(entry => ({
            ...entry,
            product_name: product.name || entry.product_name,
            item_group: product.item_group || entry.item_group,
        }))
    })
}

function buildWarehouseRows(products = [], warehouses = []) {
    const warehouseMap = new Map()

    products.forEach((product) => {
        const warehouseEntries = Array.isArray(product.warehouseEntries) ? product.warehouseEntries : []
        warehouseEntries.forEach((warehouseEntry) => {
            const warehouseId = warehouseEntry.warehouse?.id || warehouseEntry.warehouse?.name || "unknown"
            const existing = warehouseMap.get(String(warehouseId)) || {
                id: warehouseId,
                warehouseName: warehouseEntry.warehouse?.name || getWarehouseName(warehouseId, warehouses),
                units: 0,
                frontRoomUnits: 0,
                backRoomUnits: 0,
                customRoomUnits: 0,
                packageCount: 0,
                productCount: 0,
            }

            existing.units += toNumber(warehouseEntry.stock)
            existing.frontRoomUnits += toNumber(warehouseEntry.frontRoomStock)
            existing.backRoomUnits += toNumber(warehouseEntry.backRoomStock)
            existing.customRoomUnits += toNumber(warehouseEntry.customRoomStock)
            existing.packageCount += Array.isArray(warehouseEntry.batches) ? warehouseEntry.batches.length : 0
            existing.productCount += toNumber(warehouseEntry.stock) > 0 ? 1 : 0
            warehouseMap.set(String(warehouseId), existing)
        })
    })

    return Array.from(warehouseMap.values()).sort((a, b) => b.units - a.units)
}

function summarizeInventoryProducts(products = [], warehouses = [], options = {}) {
    const lowStockThreshold = options.lowStockThreshold || 5
    const ledgerEntries = flattenLedgerEntries(products)
    const totalUnits = products.reduce((total, product) => total + toNumber(product.stock), 0)
    const frontRoomUnits = products.reduce((total, product) => total + toNumber(product.totalFrontRoomStock), 0)
    const backRoomUnits = products.reduce((total, product) => total + toNumber(product.totalBackRoomStock), 0)
    const customRoomUnits = products.reduce((total, product) => total + toNumber(product.totalCustomRoomStock), 0)
    const outOfStockProducts = products.filter(product => toNumber(product.stock) <= 0)
    const lowStockProducts = products.filter((product) => {
        const stock = toNumber(product.stock)
        return stock > 0 && stock <= lowStockThreshold
    })
    const lowStockPackages = ledgerEntries.filter((entry) => {
        const quantity = getEntryQuantity(entry)
        return quantity > 0 && quantity <= lowStockThreshold
    })
    const outOfStockPackages = ledgerEntries.filter(entry => getEntryQuantity(entry) <= 0)
    const valuedLedgerEntries = ledgerEntries.filter(entry => toNumber(entry.valuation_rate) > 0)
    const estimatedInventoryValue = valuedLedgerEntries.reduce((total, entry) => total + getEntryValue(entry), 0)

    return {
        productCount: products.length,
        packageCount: ledgerEntries.length,
        totalUnits,
        frontRoomUnits,
        backRoomUnits,
        customRoomUnits,
        outOfStockProducts,
        lowStockProducts,
        lowStockPackages,
        outOfStockPackages,
        lowStockThreshold,
        estimatedInventoryValue,
        valuedLedgerEntryCount: valuedLedgerEntries.length,
        valuationCoverage: ledgerEntries.length ? valuedLedgerEntries.length / ledgerEntries.length : 0,
        warehouseRows: buildWarehouseRows(products, warehouses),
    }
}

function getReportRowKey(row = {}) {
    return [
        row.item_code || row.product_name || row.name || "unknown-product",
        row.warehouse || row.warehouseName || "unknown-warehouse",
    ].join("::")
}

function getReportUnitCost(row = {}) {
    return toNumber(row.avg_unit_stockcost || row.unit_cost || row.last_purchase_rate || row.valuation_rate)
}

function getReportUnitRetail(row = {}) {
    return toNumber(row.selling_price || row.regular_price || row.price)
}

function getReportRoomCost(row = {}) {
    const unitCost = getReportUnitCost(row)
    return unitCost > 0 ? toNumber(row.in_stock) * unitCost : null
}

function getReportRoomRetailValue(row = {}) {
    const unitRetail = getReportUnitRetail(row)
    return unitRetail > 0 ? toNumber(row.in_stock) * unitRetail : null
}

function buildReportProductGroups(rows = []) {
    const groups = new Map()

    rows.forEach((row) => {
        const key = getReportRowKey(row)
        const existing = groups.get(key) || {
            key,
            itemCode: row.item_code || "",
            name: row.display_name || row.product_name || row.item_name || row.name || "Unknown product",
            itemGroup: row.item_group || row.classification || "",
            warehouseName: row.warehouseName || row.warehouse || "Unknown Warehouse",
            stock: 0,
            inStockCost: 0,
            retailStockValue: 0,
            rawInStockCostFallback: 0,
            rawRetailValueFallback: 0,
            hasComputedCost: false,
            hasComputedRetailValue: false,
            transferIn: 0,
            transferOut: 0,
            onOrder: 0,
            daysSinceLastSold: row.days_since_last_sold,
        }

        const roomCost = getReportRoomCost(row)
        const roomRetailValue = getReportRoomRetailValue(row)

        existing.stock += toNumber(row.in_stock)
        if (roomCost !== null) {
            existing.inStockCost += roomCost
            existing.hasComputedCost = true
        } else {
            existing.rawInStockCostFallback = Math.max(existing.rawInStockCostFallback, toNumber(row.in_stockcost))
        }
        if (roomRetailValue !== null) {
            existing.retailStockValue += roomRetailValue
            existing.hasComputedRetailValue = true
        } else {
            existing.rawRetailValueFallback = Math.max(existing.rawRetailValueFallback, toNumber(row.retail_stockvalue))
        }
        existing.transferIn += toNumber(row.transferIn)
        existing.transferOut += toNumber(row.transferOut)
        existing.onOrder += toNumber(row.onOrder)
        groups.set(key, existing)
    })

    return Array.from(groups.values()).map(group => ({
        ...group,
        inStockCost: group.hasComputedCost ? group.inStockCost : group.rawInStockCostFallback,
        retailStockValue: group.hasComputedRetailValue ? group.retailStockValue : group.rawRetailValueFallback,
        rawInStockCostFallback: undefined,
        rawRetailValueFallback: undefined,
        hasComputedCost: undefined,
        hasComputedRetailValue: undefined,
    }))
}

function buildReportWarehouseRows(rows = []) {
    const warehouseMap = new Map()
    const productGroupByKey = new Map(buildReportProductGroups(rows).map(group => [group.key, group]))

    rows.forEach((row) => {
        const warehouseName = row.warehouseName || row.warehouse || "Unknown Warehouse"
        const existing = warehouseMap.get(warehouseName) || {
            id: warehouseName,
            warehouseName,
            units: 0,
            frontRoomUnits: 0,
            backRoomUnits: 0,
            customRoomUnits: 0,
            packageCount: 0,
            productCount: 0,
            costValue: 0,
            retailValue: 0,
            productKeys: new Set(),
        }
        const stock = toNumber(row.in_stock)
        const room = String(row.room || "").toLowerCase()

        existing.units += stock
        existing.packageCount += 1
        existing.productKeys.add(getReportRowKey(row))

        if (room.includes("back")) {
            existing.backRoomUnits += stock
        } else if (room.includes("front") || !room || room === "-") {
            existing.frontRoomUnits += stock
        } else {
            existing.customRoomUnits += stock
        }

        warehouseMap.set(warehouseName, existing)
    })

    return Array.from(warehouseMap.values())
        .map(row => ({
            ...row,
            productCount: row.productKeys.size,
            costValue: Array.from(row.productKeys).reduce((total, key) => {
                return total + toNumber(productGroupByKey.get(key)?.inStockCost)
            }, 0),
            retailValue: Array.from(row.productKeys).reduce((total, key) => {
                return total + toNumber(productGroupByKey.get(key)?.retailStockValue)
            }, 0),
            productKeys: undefined,
        }))
        .sort((a, b) => b.units - a.units)
}

function summarizeInventoryReportRows(rows = [], outOfStockRows = [], options = {}) {
    const lowStockThreshold = options.lowStockThreshold || 5
    const productGroups = buildReportProductGroups(rows)
    const outOfStockProducts = buildReportProductGroups(outOfStockRows)
    const totalUnits = rows.reduce((total, row) => total + toNumber(row.in_stock), 0)
    const frontRoomUnits = rows.reduce((total, row) => {
        const room = String(row.room || "").toLowerCase()
        return room.includes("back") || (room && !room.includes("front") && room !== "-")
            ? total
            : total + toNumber(row.in_stock)
    }, 0)
    const backRoomUnits = rows.reduce((total, row) => {
        const room = String(row.room || "").toLowerCase()
        return room.includes("back") ? total + toNumber(row.in_stock) : total
    }, 0)
    const customRoomUnits = rows.reduce((total, row) => {
        const room = String(row.room || "").toLowerCase()
        return room && !room.includes("front") && !room.includes("back") && room !== "-"
            ? total + toNumber(row.in_stock)
            : total
    }, 0)
    const lowStockProducts = productGroups.filter(product => product.stock > 0 && product.stock <= lowStockThreshold)
    const costValuedProducts = productGroups.filter(product => toNumber(product.inStockCost) > 0)

    return {
        productCount: productGroups.length,
        packageCount: rows.length,
        rowCount: rows.length,
        totalUnits,
        frontRoomUnits,
        backRoomUnits,
        customRoomUnits,
        outOfStockProducts,
        lowStockProducts,
        lowStockPackages: lowStockProducts,
        outOfStockPackages: outOfStockProducts,
        lowStockThreshold,
        estimatedInventoryValue: productGroups.reduce((total, product) => total + toNumber(product.inStockCost), 0),
        estimatedRetailValue: productGroups.reduce((total, product) => total + toNumber(product.retailStockValue), 0),
        valuedLedgerEntryCount: costValuedProducts.length,
        valuedRowCount: costValuedProducts.length,
        valuationCoverage: productGroups.length ? costValuedProducts.length / productGroups.length : 0,
        warehouseRows: buildReportWarehouseRows(rows),
        transferInUnits: productGroups.reduce((total, product) => total + product.transferIn, 0),
        transferOutUnits: productGroups.reduce((total, product) => total + product.transferOut, 0),
        onOrderUnits: productGroups.reduce((total, product) => total + product.onOrder, 0),
    }
}

function summarizeTransfers(transfers = [], options = {}) {
    const now = options.now ? new Date(options.now) : new Date()
    const staleAfterDays = options.staleAfterDays || 3
    const statusCounts = transfers.reduce((acc, transfer) => {
        const status = transfer.stock_transfer_status || "UNKNOWN"
        acc[status] = (acc[status] || 0) + 1
        return acc
    }, {})

    const staleTransfers = transfers.filter((transfer) => {
        const date = normalizeDate(transfer.shipped_on || transfer.updatedAt || transfer.createdAt || transfer.creation)
        if (!date) return false
        const ageInDays = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
        return ageInDays >= staleAfterDays
    })

    return {
        activeCount: transfers.length,
        staleCount: staleTransfers.length,
        pendingCount: statusCounts.PENDING || 0,
        shippedCount: statusCounts.SHIPPED || 0,
        transferredCount: statusCounts.TRANSFERRED || 0,
        grandTotal: transfers.reduce((total, transfer) => total + toNumber(transfer.grand_total), 0),
        statusCounts,
        recentTransfers: transfers.slice(0, 5),
        staleTransfers,
    }
}

function summarizeAdjustments(quantityAdjustments = [], priceAdjustments = []) {
    const quantityRows = quantityAdjustments.map(adjustment => ({
        ...adjustment,
        adjustmentType: "Quantity",
    }))
    const priceRows = priceAdjustments.map(adjustment => ({
        ...adjustment,
        adjustmentType: "Price",
    }))
    const allAdjustments = quantityRows
        .concat(priceRows)
        .sort((a, b) => {
            const dateA = normalizeDate(a.createdAt || a.updatedAt || a.formattedCreatedAt)
            const dateB = normalizeDate(b.createdAt || b.updatedAt || b.formattedCreatedAt)
            return (dateB?.getTime() || 0) - (dateA?.getTime() || 0)
        })

    return {
        quantityCount: quantityRows.length,
        priceCount: priceRows.length,
        totalCount: allAdjustments.length,
        netQuantityCostChange: quantityRows.reduce((total, row) => total + toNumber(row.netChange), 0),
        netPriceChange: priceRows.reduce((total, row) => total + toNumber(row.netChange), 0),
        recentAdjustments: allAdjustments.slice(0, 5),
    }
}

function summarizeImportEntries(entries = []) {
    const sortedEntries = entries
        .slice()
        .sort((a, b) => {
            const dateA = normalizeDate(a.createdAt || a.creation || a.formattedCreatedAt)
            const dateB = normalizeDate(b.createdAt || b.creation || b.formattedCreatedAt)
            return (dateB?.getTime() || 0) - (dateA?.getTime() || 0)
        })

    return {
        totalEntries: sortedEntries.length,
        latestEntry: sortedEntries[0] || null,
        recentEntries: sortedEntries.slice(0, 5),
    }
}

export {
    buildWarehouseRows,
    flattenLedgerEntries,
    getEntryQuantity,
    getEntryValue,
    summarizeInventoryReportRows,
    summarizeAdjustments,
    summarizeImportEntries,
    summarizeInventoryProducts,
    summarizeTransfers,
    toNumber,
}
